import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Check, Phone, ChevronsDown } from "lucide-react";
import { SlideshowSpot } from "@/components/SlideshowSpot";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ServiceProjectGallery,
  ProjectDetailModal,
  useCategoryProjects,
  projectSlug,
  type GalleryProject,
} from "@/components/ServiceProjectGallery";


const SERVICES_TITLE =
  "Remodeling Services in Searcy AR | Homestead Construction";
const SERVICES_DESC =
  "Home additions, basement finishing, garage conversions, kitchen and bath remodels, and exterior work in Searcy and Northeast Arkansas.";

export const Route = createFileRoute("/services")({
  validateSearch: (search: Record<string, unknown>): { project?: string } =>
    typeof search['project'] === "string" && search['project']
      ? { project: search['project'] as string }
      : {},
  head: () => ({
    meta: [
      { title: SERVICES_TITLE },
      { name: "description", content: SERVICES_DESC },
      { property: "og:title", content: SERVICES_TITLE },
      { property: "og:description", content: SERVICES_DESC },
      { property: "og:url", content: "https://homesteadconstruction.us/services" },
      { property: "og:type", content: "website" },
      { name: "twitter:title", content: SERVICES_TITLE },
      { name: "twitter:description", content: SERVICES_DESC },
    ],
    links: [{ rel: "canonical", href: "https://homesteadconstruction.us/services" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: "Home Remodeling and Construction",
          provider: {
            "@type": "GeneralContractor",
            name: "Homestead Construction of NEA",
            telephone: "+1-501-593-6361",
            address: {
              "@type": "PostalAddress",
              addressLocality: "Searcy",
              addressRegion: "AR",
              addressCountry: "US",
            },
          },
          areaServed: [
            { "@type": "City", name: "Searcy" },
            { "@type": "AdministrativeArea", name: "Northeast Arkansas" },
          ],
          hasOfferCatalog: {
            "@type": "OfferCatalog",
            name: "Homestead Construction Services",
            itemListElement: [
              { "@type": "Offer", itemOffered: { "@type": "Service", name: "Room Additions" } },
              { "@type": "Offer", itemOffered: { "@type": "Service", name: "Property Extensions" } },
              { "@type": "Offer", itemOffered: { "@type": "Service", name: "Basement Finishing" } },
              { "@type": "Offer", itemOffered: { "@type": "Service", name: "Garage Conversions" } },
              { "@type": "Offer", itemOffered: { "@type": "Service", name: "Kitchen Remodels" } },
              { "@type": "Offer", itemOffered: { "@type": "Service", name: "Bathroom Remodels" } },
              { "@type": "Offer", itemOffered: { "@type": "Service", name: "Interior Remodeling" } },
              { "@type": "Offer", itemOffered: { "@type": "Service", name: "Decks and Patios" } },
              { "@type": "Offer", itemOffered: { "@type": "Service", name: "Flooring" } },
              { "@type": "Offer", itemOffered: { "@type": "Service", name: "Painting" } },
              { "@type": "Offer", itemOffered: { "@type": "Service", name: "Custom Builds" } },
            ],
          },
        }),
      },
    ],
  }),
  component: ServicesPage,
});

type SubService = { title: string; body: string; bullets: string[] };
type Category = {
  id: string;
  label: string;
  title: string;
  slotKey: string;
  galleryPrefix: string;
  intro: string;
  subs: SubService[];
};

const GALLERY_PER_CAT = 8;

const categories: Category[] = [
  {
    id: "home-upgrades",
    label: "Home Upgrades",
    title: "Home Upgrades",
    slotKey: "services.cat.upgrades",
    galleryPrefix: "services.gallery.upgrades",
    intro:
      "Whether you need more space for a growing family, a dedicated area for hobbies, or simply want to enhance your home's functionality and value, Homestead Construction specializes in seamless home upgrades and thoughtful expansions. We transform your existing property to perfectly match your evolving lifestyle.",
    subs: [
      {
        title: "Room Additions",
        body: "Need an extra bedroom, a dedicated home office, or a spacious family room? Our expert team designs and builds seamless room additions that blend perfectly with your existing home's architecture and style, ensuring a cohesive look and feel.",
        bullets: [
          "Seamless integration with existing structure",
          "Custom design to match your home's aesthetic",
          "Increased living space and property value",
        ],
      },
      {
        title: "Property Extensions",
        body: "Expand your home's footprint with our professional property extension services. From sunrooms and conservatories to multi-story additions, we handle all aspects of the project, ensuring structural integrity and beautiful finishes.",
        bullets: [
          "Sunrooms, conservatories, and multi-story additions",
          "Enhanced outdoor-indoor living transitions",
          "Expert project management from start to finish",
        ],
      },
      {
        title: "Basement Finishing",
        body: "Unlock the full potential of your home by transforming your unfinished basement into a vibrant, functional living area. We can create anything from extra bedrooms and bathrooms to home theaters, gyms, or playrooms.",
        bullets: [
          "Convert unused space into valuable living areas",
          "Custom layouts for bedrooms, entertainment, or fitness",
          "Increase home value and usable square footage",
        ],
      },
      {
        title: "Garage Conversions",
        body: "Transform your underutilized garage into a functional and comfortable living space. Ideal for guest suites, home offices, studios, or additional bedrooms, a garage conversion is a cost-effective way to add significant value and utility to your home.",
        bullets: [
          "Cost-effective way to add living space",
          "Perfect for guest suites, offices, or studios",
          "Maximizes existing property footprint",
        ],
      },
    ],
  },
  {
    id: "remodels",
    label: "Remodels",
    title: "Remodels",
    slotKey: "services.cat.remodels",
    galleryPrefix: "services.gallery.remodels",
    intro:
      "At Homestead Construction, we specialize in transforming your existing spaces into modern, functional, and beautiful environments that perfectly reflect your style and enhance your home's overall appeal and value.",
    subs: [
      {
        title: "Kitchen Remodels",
        body: "Design and build the kitchen of your dreams - a space that's both aesthetically pleasing and highly practical for your culinary needs. From modern gourmet kitchens to cozy family hubs, we manage the entire process to deliver your ideal cooking and gathering space.",
        bullets: [
          "Custom cabinet installation",
          "Countertop and backsplash solutions",
          "Appliance integration and layout optimization",
        ],
      },
      {
        title: "Bathroom Remodels",
        body: "Create a luxurious and functional oasis with our custom bathroom design and renovation services. Whether you desire a spa-like retreat or a highly efficient family bathroom, we handle every detail from plumbing to fixtures and finishes.",
        bullets: [
          "Custom showers and tubs",
          "Vanity and countertop installation",
          "Efficient space planning and design",
        ],
      },
      {
        title: "Interior Remodeling",
        body: "Revitalize your home's interior with our custom renovation services. From creating open-concept living areas to designing bespoke built-ins and updating finishes, we craft spaces that are both beautiful and highly functional for your daily life.",
        bullets: [
          "Open-concept conversions",
          "Custom cabinetry and built-ins",
          "Flooring, trim, and paint updates",
        ],
      },
      {
        title: "General Remodeling & Renovations",
        body: "Beyond specific rooms, we offer comprehensive general remodeling services for any area of your home. Whether it's a full home renovation, basement finishing, or reconfiguring interior layouts, our team ensures a cohesive and high-quality transformation.",
        bullets: [
          "Whole-home renovations",
          "Layout reconfigurations",
          "Seamless integration of new and existing elements",
        ],
      },
    ],
  },
  {
    id: "finishing-exterior",
    label: "Finishing & Exterior",
    title: "Finishing & Exterior",
    slotKey: "services.cat.exterior",
    galleryPrefix: "services.gallery.exterior",
    intro:
      "Elevate your home's aesthetics, functionality, and durability with Homestead Constructions' expert finishing and exterior services. We focus on the details that make a significant impact, both inside and out.",
    subs: [
      {
        title: "Decks & Patios",
        body: "Extend your living space outdoors with a custom-designed and expertly constructed deck or patio. We build durable, beautiful, and functional outdoor areas perfect for entertaining, relaxation, and enjoying your property.",
        bullets: [
          "Custom designs for any outdoor space",
          "High-quality, weather-resistant materials",
          "Enhances outdoor entertaining and relaxation",
        ],
      },
      {
        title: "Flooring",
        body: "Upgrade your home's interior with stunning and durable flooring solutions. We offer expert installation of a wide range of materials, including hardwood, tile, laminate, and more, ensuring a flawless finish that complements your style.",
        bullets: [
          "Hardwood, tile, laminate, and vinyl options",
          "Precision installation for lasting beauty",
          "Enhances interior aesthetics and comfort",
        ],
      },
      {
        title: "Painting",
        body: "Refresh and enhance your home's appearance with our professional painting services. We provide meticulous preparation and application for both interior and exterior surfaces, ensuring a flawless, long-lasting finish that transforms your space.",
        bullets: [
          "Expert color consultation and selection",
          "Thorough surface preparation",
          "High-quality paints and finishes for durability",
        ],
      },
      {
        title: "Finishing & Trim Work",
        body: "The right finishing touches can elevate any space. We offer comprehensive general finishing services, including custom trim work, crown molding, baseboards, and other details that add character and sophistication to your home.",
        bullets: [
          "Custom trim and molding installation",
          "Detailed carpentry and millwork",
          "Enhances architectural appeal",
        ],
      },
    ],
  },
  {
    id: "custom-projects",
    label: "Custom Projects",
    title: "Custom Projects",
    slotKey: "services.cat.custom",
    galleryPrefix: "services.gallery.custom",
    intro:
      "At Homestead Construction, we thrive on bringing unique visions to life. If your project doesn't fit a standard category, our expertise in custom construction ensures that even the most specialized and intricate ideas are executed with precision, quality, and transparent communication.",
    subs: [
      {
        title: "Custom Builds",
        body: "Enhance your outdoor living with custom structures such as pergolas, gazebos, detached garages, or pool houses. We design and build exterior projects that complement your home's architecture and extend your usable space.",
        bullets: [
          "Custom pergolas, gazebos, and outdoor kitchens",
          "Detached garages and workshops",
          "Unique built-in shelving and cabinetry",
          "Custom ceiling and wall treatments",
          "Custom libraries and offices",
        ],
      },
    ],
  },
];




function ServicesPage() {
  const { project: projectParam } = Route.useSearch();
  const navigate = useNavigate({ from: "/services" });
  const [tab, setTab] = useState(categories[0].id);

  // One query per category title (static list, so hook order is stable).
  const p0 = useCategoryProjects(categories[0].title);
  const p1 = useCategoryProjects(categories[1].title);
  const p2 = useCategoryProjects(categories[2].title);
  const p3 = useCategoryProjects(categories[3].title);
  const byCat: GalleryProject[][] = [p0, p1, p2, p3];
  const allProjects = [...p0, ...p1, ...p2, ...p3];

  const selected =
    allProjects.find((p) => projectSlug(p.name) === projectParam) ?? null;

  // When the URL names a project, switch to the tab that owns it.
  useEffect(() => {
    if (!selected) return;
    const idx = categories.findIndex((c) => c.title === selected.category);
    if (idx >= 0) setTab(categories[idx].id);
  }, [selected]);

  const openProject = (p: GalleryProject) =>
    void navigate({ search: { project: projectSlug(p.name) } });
  const closeProject = () => void navigate({ search: {} });

  return (
    <>
      {/* Hero */}
      <section className="relative bg-ink text-white overflow-hidden">
        <div className="absolute inset-0">
          <SlideshowSpot slideshowKey="services.hero" fallbackSlotKeys={["services.hero"]} label="Services Hero" />
          <div className="absolute inset-0 bg-ink/70" />
        </div>
        <div className="container mx-auto px-4 py-24 md:py-32 relative flex flex-col items-center text-center">
          <h1 className="font-display font-bold uppercase text-4xl md:text-5xl tracking-wide text-white">
            Our Services
          </h1>
          <ChevronsDown className="mt-6 h-7 w-7 text-brand" />
        </div>
      </section>

      {/* Intro */}
      <section className="container mx-auto px-4 py-16 text-center">
        <p className="eyebrow">Our Services</p>
        <h2 className="font-display font-bold uppercase text-3xl md:text-4xl lg:text-5xl text-ink mt-3 max-w-4xl mx-auto leading-tight">
          Comprehensive Home Improvement Services Tailored to Your Lifestyle
        </h2>
        <div className="mt-6 inline-flex items-center gap-2 text-ink/70">
          <Check className="h-5 w-5 text-brand" />
          <span>Business License Number 0450850225</span>
        </div>
      </section>

      {/* Tabs */}
      <section className="bg-surface-alt py-16">
        <div className="container mx-auto px-4">
          <Tabs value={tab} onValueChange={setTab} className="w-full">
            <TabsList className="mx-auto flex h-auto flex-wrap justify-center gap-2 bg-transparent p-0 mb-10">
              {categories.map((c) => (
                <TabsTrigger
                  key={c.id}
                  value={c.id}
                  className="font-display font-semibold uppercase tracking-wide px-6 py-3 data-[state=active]:bg-brand data-[state=active]:text-white"
                >
                  {c.label}
                </TabsTrigger>
              ))}
            </TabsList>

            {categories.map((cat, i) => (
              <TabsContent key={cat.id} value={cat.id} className="mt-0">
                <CategoryPanel
                  cat={cat}
                  projects={byCat[i] ?? []}
                  onSelectProject={openProject}
                />
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>

      {/* CTA Band */}
      <section className="relative bg-ink text-white overflow-hidden">
        <div className="absolute inset-0">
          <SlideshowSpot slideshowKey="services.cta" fallbackSlotKeys={["services.cta"]} label="Services CTA Band" />
        </div>
        <div className="absolute inset-0 bg-black/65" aria-hidden="true" />
        <div className="relative container mx-auto px-4 py-24 text-center">
          <p className="eyebrow text-brand">Your Vision. Our Craft.</p>
          <p className="font-display text-xl md:text-2xl text-white mt-4 max-w-2xl mx-auto leading-relaxed">
            Transforming houses into dream homes with unparalleled quality and transparent service. Contact us to start your project today!
          </p>
          <div className="mt-8">
            <Button asChild variant="brand" size="xl">
              <a href="tel:5015936361"><Phone className="h-4 w-4" /> Call Us</a>
            </Button>
          </div>
        </div>
      </section>

      <ProjectDetailModal project={selected} onClose={closeProject} />
    </>
  );
}


function CategoryPanel({
  cat,
  projects,
  onSelectProject,
}: {
  cat: Category;
  projects: GalleryProject[];
  onSelectProject: (p: GalleryProject) => void;
}) {
  return (
    <article>
      {/* Header: image + intro */}
      <div className="grid md:grid-cols-2 gap-8 items-center">
        <div className="aspect-[4/3] overflow-hidden rounded">
          <SlideshowSpot slideshowKey={cat.slotKey} fallbackSlotKeys={[cat.slotKey]} label={cat.title} />
        </div>
        <div>
          <p className="eyebrow">Our Services</p>
          <h3 className="font-display font-bold uppercase text-3xl md:text-4xl text-ink mt-2 leading-tight">
            {cat.title}
          </h3>
          <p className="text-ink/70 mt-5 leading-relaxed">{cat.intro}</p>
          <Button asChild variant="brand" size="lg" className="mt-6">
            <a href="tel:5015936361">
              <Phone className="h-4 w-4" />
              Call Us
            </a>
          </Button>
        </div>
      </div>

      {/* Sub-services */}
      <div className="grid md:grid-cols-2 gap-6 mt-12">
        {cat.subs.map((sub) => (
          <div key={sub.title} className="bg-white p-6 rounded shadow-sm border-t-4 border-brand">
            <h4 className="font-display font-bold uppercase text-lg text-ink">{sub.title}</h4>
            <p className="text-ink/70 mt-3 leading-relaxed text-sm">{sub.body}</p>
            <ul className="mt-4 space-y-2">
              {sub.bullets.map((b) => (
                <li key={b} className="flex items-start gap-2 text-sm text-ink">
                  <Check className="h-4 w-4 text-brand flex-shrink-0 mt-0.5" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <ServiceProjectGallery projects={projects} onSelect={onSelectProject} />


    </article>
  );
}

